/**
 * เสียงแจ้งเตือน — รองรับทั้งเสียงในตัว (สังเคราะห์เอง) และไฟล์ที่ admin อัปโหลด
 *
 * ไฟล์ที่อัปโหลดเล่นผ่าน <audio> ไม่ใช่ fetch + decodeAudioData
 * เหตุผล: bucket ของ DigitalOcean Spaces ไม่ได้เปิด CORS (preflight ตอบ 403)
 * ถ้าใช้ fetch เบราว์เซอร์จะบล็อกแล้วเสียงที่อัปโหลดไม่ดังเลย
 * ส่วน <audio> เล่นข้ามโดเมนได้โดยไม่ต้องมี CORS เหมือน <img>
 *
 * เสียงในตัวยังใช้ Web Audio สังเคราะห์เอง — ไม่ต้องมีไฟล์ ใช้ได้เสมอแม้เน็ตหลุด
 *
 * ข้อจำกัดเบราว์เซอร์: เล่นเสียงไม่ได้จนกว่าผู้ใช้จะคลิกอะไรสักอย่างก่อน (autoplay policy)
 * → ต้องเรียก unlockAudio() จากใน onClick เท่านั้น
 */

const STORAGE_KEY = 'caseSoundEnabled';

let ctx = null;
let unlocked = false;

// เก็บ <audio> ต่อ URL ไว้ใช้ซ้ำ — เสียง "ถือเคสนาน" ดังทุก 10 วิ
// ถ้าสร้างใหม่ทุกครั้งเบราว์เซอร์จะโหลดไฟล์ซ้ำไม่จบ
const audioCache = new Map();

export const isSoundEnabled = () => localStorage.getItem(STORAGE_KEY) === '1';
export const setSoundEnabled = (on) => localStorage.setItem(STORAGE_KEY, on ? '1' : '0');

export const unlockAudio = async () => {
  try {
    if (!ctx) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) ctx = new AudioCtx();
    }
    if (ctx && ctx.state === 'suspended') await ctx.resume();
    // ถือว่าปลดล็อกแล้วเมื่อถูกเรียกจาก user gesture — <audio> อาศัย user activation
    // ระดับเอกสาร ไม่ได้ผูกกับ AudioContext จึงไม่บังคับว่า ctx ต้องพร้อม
    unlocked = true;
    return true;
  } catch (e) {
    return false;
  }
};

export const isAudioReady = () => unlocked;

/**
 * ปลดล็อกอัตโนมัติเมื่อผู้ใช้คลิก/กดปุ่มอะไรก็ได้ครั้งแรกในหน้า
 *
 * จำเป็นเพราะ "สวิตช์เปิดเสียง" เก็บใน localStorage (จำข้ามรอบได้) แต่การปลดล็อก
 * ของเบราว์เซอร์ผูกกับ user gesture ของหน้านั้น ๆ (จำข้ามรอบไม่ได้)
 * ถ้าไม่มีตัวนี้ พอ refresh ปุ่มจะโชว์ว่าเปิดอยู่แต่เสียงไม่ดัง จนกว่าจะกดปิด-เปิดใหม่
 *
 * @param {function} [onUnlocked] เรียกเมื่อปลดล็อกสำเร็จ (ไว้อัปเดตหน้าจอ)
 */
let autoUnlockInstalled = false;
export const installAutoUnlock = (onUnlocked) => {
  if (autoUnlockInstalled || unlocked) return;
  autoUnlockInstalled = true;

  const events = ['pointerdown', 'keydown', 'touchstart'];
  const handler = async () => {
    events.forEach((e) => window.removeEventListener(e, handler, true));
    const ok = await unlockAudio();
    if (ok && typeof onUnlocked === 'function') onUnlocked();
  };
  // capture = true เพื่อให้ทำงานก่อนที่ component อื่นจะ stopPropagation
  events.forEach((e) => window.addEventListener(e, handler, true));
};

const getAudio = (url) => {
  let el = audioCache.get(url);
  if (!el) {
    el = new Audio(url);
    el.preload = 'auto';
    audioCache.set(url, el);
  }
  return el;
};

/** เสียงในตัว: ติ๊ง 2 โน้ตสั้น ๆ — โทนสูง = งานใหม่, โทนต่ำ = เตือนถือเคสนาน */
const playBuiltIn = (volume, urgent) => {
  if (!ctx || ctx.state !== 'running') return;
  const now = ctx.currentTime;
  const notes = urgent
    ? [{ freq: 440.0, at: 0 }, { freq: 349.23, at: 0.16 }]
    : [{ freq: 880.0, at: 0 }, { freq: 1174.66, at: 0.13 }];

  notes.forEach(({ freq, at }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    // ไล่ระดับขึ้น-ลง ไม่ตัดกะทันหัน ไม่งั้นได้ยินเสียง "ป๊อก" ตอนหัว/ท้าย
    gain.gain.setValueAtTime(0.0001, now + at);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.01, volume), now + at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.32);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + at);
    osc.stop(now + at + 0.34);
  });
};

/**
 * เล่นเสียงแจ้งเตือน
 * @param {object} opts
 * @param {string} [opts.soundUrl] ไฟล์ที่ admin อัปโหลด (ไม่มี = ใช้เสียงในตัว)
 * @param {number} [opts.volume]   0.1–1
 * @param {boolean} [opts.urgent]  ใช้โทนเร่งของเสียงในตัว
 * @param {boolean} [opts.force]   เล่นแม้ผู้ใช้ปิดเสียงไว้ (ใช้ตอนฟังตัวอย่างในหน้า admin)
 */
export const playNotificationSound = ({
  soundUrl = null,
  volume = 0.8,
  urgent = false,
  force = false,
} = {}) => {
  if (!force && !isSoundEnabled()) return;
  if (!unlocked) return;

  const vol = Math.max(0.01, Math.min(1, Number(volume) || 0.8));

  if (soundUrl) {
    try {
      const el = getAudio(soundUrl);
      el.volume = vol;
      el.currentTime = 0; // เล่นซ้ำจากต้นได้แม้รอบก่อนยังไม่จบ
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          // ไฟล์เสีย/โหลดไม่ขึ้น/ถูกบล็อก → ถอยไปเสียงในตัว ดีกว่าเงียบแล้วไม่มีใครรู้ตัว
          // แต่ log ไว้ด้วย ไม่ให้ปัญหาถูกกลบเงียบเหมือนรอบที่แล้ว
          console.warn('[notificationSound] เล่นไฟล์ที่อัปโหลดไม่ได้ → ใช้เสียงในตัวแทน', err);
          playBuiltIn(vol, urgent);
        });
      }
      return;
    } catch (err) {
      console.warn('[notificationSound] เล่นไฟล์ที่อัปโหลดไม่ได้ → ใช้เสียงในตัวแทน', err);
    }
  }

  playBuiltIn(vol, urgent);
};

/** ล้าง cache เมื่อ admin เปลี่ยนไฟล์เสียง จะได้ไม่เล่นของเก่าค้าง */
export const clearSoundCache = () => audioCache.clear();

// ชื่อเดิม — ยังมีที่เรียกอยู่ใน Navbar ตอนกดเปิดเสียงเพื่อฟังตัวอย่าง
export const playNewWorkSound = (opts = {}) => playNotificationSound(opts);
